import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, Clock, TrendingDown, Store, HelpCircle, SlidersHorizontal, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { vitrineAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import WeekScroller from '@/components/sales/WeekScroller';

const CLOSE_HOURS = ['19:00', '19:30', '20:00', '20:30', '21:00'];

function mondayOfIso(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    return d.toISOString().slice(0, 10);
}

function daysAgoIso(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function VitrineTab({ sites, active }) {
    const { user } = useAuth();
    // Site restriction: users with allowed_sites only see the vitrine of their own site(s)
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const feSites = useMemo(
        () => sites.filter(s => s.name?.startsWith('FE ') && (!allowedIds || allowedIds.includes(s.id))),
        [sites, allowedIds]
    );
    const [status, setStatus] = useState(null);
    const [siteId, setSiteId] = useState(null);
    const [startDate, setStartDate] = useState(daysAgoIso(30));
    const [endDate, setEndDate] = useState(todayIso());
    const [closeHour, setCloseHour] = useState('19:30');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [periodDay, setPeriodDay] = useState(null); // day view inside period mode (keeps the scroller visible)
    const [dayDate, setDayDate] = useState(todayIso());
    const [dayData, setDayData] = useState(null);
    const [showAnomalies, setShowAnomalies] = useState(false);
    const [showUnknown, setShowUnknown] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showEan, setShowEan] = useState(false);

    // 7 day-shortcut buttons when the selection is exactly one week
    const weekDays = useMemo(() => {
        if (!startDate || !endDate) return null;
        const s = new Date(startDate + 'T00:00:00');
        const e = new Date(endDate + 'T00:00:00');
        if (e - s !== 6 * 86400000) return null;
        const labels = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        const out = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(s);
            d.setDate(d.getDate() + i);
            out.push({ iso: d.toISOString().slice(0, 10), label: labels[d.getDay()] });
        }
        return out;
    }, [startDate, endDate]);

    const loadStatus = async () => {
        try {
            const r = await vitrineAPI.status();
            setStatus(r.data);
            const allowedFe = new Set(feSites.map(s => s.id));
            const coverageAllowed = (r.data.coverage || []).filter(c => allowedFe.has(c.site_id));
            if (!siteId && coverageAllowed.length) {
                const first = coverageAllowed[0];
                setSiteId(first.site_id);
                // Default selection: the last week that has data
                if (first.max_date) {
                    const mon = mondayOfIso(first.max_date);
                    const sun = new Date(mon + 'T00:00:00');
                    sun.setDate(sun.getDate() + 6);
                    setStartDate(mon);
                    setEndDate(sun.toISOString().slice(0, 10));
                }
            } else if (!siteId) {
                const dillon = feSites.find(s => s.name === 'FE Dillon');
                if (dillon) setSiteId(dillon.id);
                else if (feSites.length) setSiteId(feSites[0].id);
            }
            return r.data;
        } catch (e) { return null; }
    };

    useEffect(() => { if (feSites.length) loadStatus(); /* eslint-disable-next-line */ }, [feSites.length]);

    const loadSummary = async () => {
        if (!siteId) return;
        setLoading(true);
        try {
            const r = await vitrineAPI.summary({ site_id: siteId, start_date: startDate, end_date: endDate, close_hour: closeHour });
            setData(r.data);
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally { setLoading(false); }
    };

    useEffect(() => { if (siteId) loadSummary(); /* eslint-disable-next-line */ }, [siteId, startDate, endDate, closeHour]);

    const loadDay = async () => {
        if (!siteId) return;
        try {
            const r = await vitrineAPI.day({ site_id: siteId, date: dayDate });
            setDayData(r.data);
        } catch (e) { toast.error('Erreur de chargement'); }
    };
    useEffect(() => { if (periodDay && siteId) loadDay(); /* eslint-disable-next-line */ }, [periodDay, siteId, dayDate]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await vitrineAPI.sync({ since: '2026-04-01' });
            toast.info('Import lancé en arrière-plan...');
            // Poll until done
            const poll = async (attempt = 0) => {
                const st = await loadStatus();
                if (st?.last?.status === 'running' && attempt < 60) {
                    setTimeout(() => poll(attempt + 1), 3000);
                } else {
                    setSyncing(false);
                    if (st?.last?.status === 'done') {
                        toast.success(`Import terminé : ${st.last.rows_imported || 0} lignes / ${st.last.days_imported || 0} jours`);
                        loadSummary();
                    } else if (st?.last?.status === 'failed') {
                        toast.error('Import échoué');
                    }
                }
            };
            setTimeout(() => poll(), 3000);
        } catch (e) {
            toast.error('Erreur de synchronisation');
            setSyncing(false);
        }
    };

    const coverage = status?.coverage?.find(c => c.site_id === siteId);
    const kpis = data?.kpis;
    const recentSince = daysAgoIso(7);
    const recentAnomalies = useMemo(
        () => (data?.anomalies || []).filter(a => a.date >= recentSince).length,
        [data, recentSince]
    );

    return (
        <div className="space-y-3" data-testid="vitrine-tab">
            {/* Filters bar */}
            <Card>
                <CardContent className="pt-4 pb-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex flex-wrap items-center gap-1.5" data-testid="vitrine-site-shortcuts">
                            {feSites.map(s => {
                                const short = s.name.replace('FE ', '');
                                const hasData = status?.coverage?.some(c => c.site_id === s.id);
                                const isActive = siteId === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => setSiteId(s.id)}
                                        disabled={!hasData}
                                        title={hasData ? s.name : `${s.name} — aucune donnée vitrine`}
                                        className={`px-3 h-7 rounded-full text-xs font-semibold border ${isActive ? 'bg-slate-900 text-white border-slate-900' : hasData ? 'bg-white text-slate-700 border-slate-200' : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'}`}
                                        data-testid={`vitrine-site-${short.toLowerCase()}`}
                                    >{short}</button>
                                );
                            })}
                        </div>
                        <div className="flex-1" />
                        <button
                            type="button"
                            onClick={() => setShowEan(v => !v)}
                            className={`inline-flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-semibold ${showEan ? 'bg-slate-100 text-slate-700 border-slate-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                            title={showEan ? 'Masquer la colonne des codes EAN' : 'Afficher la colonne des codes EAN'}
                            data-testid="vitrine-toggle-ean"
                        >
                            {showEan ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            EAN
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(v => !v)}
                            className={`inline-flex items-center gap-1 px-2 h-7 rounded-md border text-[11px] font-semibold ${showAdvanced ? 'bg-slate-100 text-slate-700 border-slate-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                            data-testid="vitrine-advanced-toggle"
                        >
                            <SlidersHorizontal className="w-3.5 h-3.5" />
                            Options
                            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing} data-testid="vitrine-sync-btn">
                            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                            Importer les rapports
                        </Button>
                    </div>
                    <WeekScroller
                            coverageDates={coverage?.days || []}
                            minDate={coverage?.min_date}
                            maxDate={coverage?.max_date}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(s, e) => { setStartDate(s); setEndDate(e); setPeriodDay(null); }}
                        />
                        {weekDays && (
                            <div className="flex flex-wrap items-center justify-center gap-1.5" data-testid="vitrine-week-days">
                                <span className="text-[10px] text-slate-400 font-semibold uppercase mr-0.5">Voir un jour :</span>
                                {weekDays.map(d => {
                                    const hasData = coverage?.days?.includes(d.iso);
                                    const isActive = periodDay === d.iso;
                                    return (
                                        <button
                                            key={d.iso}
                                            type="button"
                                            onClick={() => {
                                                if (!hasData) return;
                                                if (isActive) { setPeriodDay(null); return; }
                                                setDayDate(d.iso);
                                                setPeriodDay(d.iso);
                                            }}
                                            disabled={!hasData}
                                            title={hasData ? (isActive ? 'Revenir à la vue période' : `Vue jour du ${d.iso}`) : `${d.iso} — pas de données`}
                                            className={`shrink-0 px-4 py-0.5 rounded-md border text-center leading-tight ${isActive ? 'bg-slate-900 text-white border-slate-900' : hasData ? 'bg-white text-slate-700 border-slate-200 hover:border-slate-500 hover:bg-slate-50' : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'}`}
                                            data-testid={`vitrine-day-shortcut-${d.iso}`}
                                        >
                                            <div className="text-[10px] font-bold">{d.label}</div>
                                            <div className={`text-[8px] ${isActive ? 'text-slate-300' : 'text-slate-400'}`}>{d.iso.slice(8)}/{d.iso.slice(5, 7)}</div>
                                        </button>
                                    );
                                })}
                                {periodDay && (
                                    <button
                                        type="button"
                                        onClick={() => setPeriodDay(null)}
                                        className="ml-1 px-2 h-6 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-500 hover:border-slate-400"
                                        data-testid="vitrine-back-to-period"
                                    >← Semaine</button>
                                )}
                            </div>
                        )}
                        {showAdvanced && (
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <div className="flex items-center gap-1">
                                {[['7j', 7], ['30j', 30], ['90j', 90]].map(([label, n]) => (
                                    <button
                                        key={label}
                                        type="button"
                                        onClick={() => { setStartDate(daysAgoIso(n)); setEndDate(todayIso()); }}
                                        className="px-2 h-6 rounded border border-slate-200 bg-white text-slate-600 text-[11px] font-semibold hover:border-slate-400"
                                        data-testid={`vitrine-range-${label}`}
                                    >{label}</button>
                                ))}
                                {coverage && (
                                    <button
                                        type="button"
                                        onClick={() => { setStartDate(coverage.min_date); setEndDate(coverage.max_date); }}
                                        className="px-2 h-6 rounded border border-slate-200 bg-white text-slate-600 text-[11px] font-semibold hover:border-slate-400"
                                        data-testid="vitrine-range-all"
                                    >Tout ({coverage.days_count}j)</button>
                                )}
                            </div>
                            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-7 w-[135px] text-xs" data-testid="vitrine-start-date" />
                            <span className="text-slate-400">→</span>
                            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-7 w-[135px] text-xs" data-testid="vitrine-end-date" />
                            <div className="flex items-center gap-1.5 ml-2">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-slate-500">Vendu jusqu'au soir si dern. vente ≥</span>
                                <Select value={closeHour} onValueChange={setCloseHour}>
                                    <SelectTrigger className="h-7 w-[80px] text-xs" data-testid="vitrine-close-hour">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CLOSE_HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        )}
                </CardContent>
            </Card>

            {!status?.coverage?.length && !syncing && (
                <Card>
                    <CardContent className="py-10 text-center text-slate-400 text-sm">
                        <Store className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        Aucune donnée vitrine importée. Cliquez sur « Importer les rapports » pour récupérer les rapports CA_Article depuis la boîte mail.
                    </CardContent>
                </Card>
            )}

            {!periodDay && kpis && (
                <>
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="vitrine-kpis">
                        <KpiCard label="Jours analysés" value={kpis.days_count} sub={kpis.first_date ? `${kpis.first_date.slice(5)} → ${kpis.last_date.slice(5)}` : ''} />
                        <KpiCard label="CA total" value={formatCurrency(kpis.ca_total)} tone="emerald" />
                        <KpiCard label="CA plein tarif" value={formatCurrency(kpis.ca_natif)} sub={`${kpis.articles_natif} articles`} />
                        <KpiCard label="CA démarque" value={formatCurrency(kpis.ca_dem)} sub={`${kpis.pct_dem_ca}% du CA · ${kpis.articles_dem} art.`} tone="amber" />
                        <KpiCard label="Perte démarque estimée" value={formatCurrency(kpis.loss_total)} tone="red" />
                    </div>

                    {/* Products table */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                Produits en vitrine
                                <Badge variant="outline" className="text-[10px]">{data.products.length}</Badge>
                            </CardTitle>
                            <p className="text-[11px] text-slate-400">
                                « Dern. vente (méd.) » : heure médiane de dernière vente au plein tarif. « Soir » : % de jours où le produit s'est vendu jusqu'à {closeHour} ou plus tard.
                            </p>
                        </CardHeader>
                        <CardContent className="overflow-x-auto p-0">
                            {loading ? (
                                <div className="py-8 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-300" /></div>
                            ) : (
                                <table className="w-full text-[11px]" data-testid="vitrine-products-table">
                                    <thead className="bg-slate-50 border-b sticky top-0">
                                        <tr className="text-[10px] uppercase text-slate-500">
                                            <th className="text-left px-2 py-1">Produit</th>
                                            <th className="text-right px-2 py-1 w-12">Jours</th>
                                            <th className="text-right px-2 py-1 w-16">Qté natif</th>
                                            <th className="text-right px-2 py-1 w-20">CA natif</th>
                                            <th className="text-right px-2 py-1 w-16">Qté dém.</th>
                                            <th className="text-right px-2 py-1 w-14">% dém.</th>
                                            <th className="text-right px-2 py-1 w-20">Perte</th>
                                            <th className="text-center px-2 py-1 w-24">Dern. vente (méd.)</th>
                                            <th className="text-center px-2 py-1 w-16">Soir</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.products.map((p, i) => (
                                            <tr key={p.libelle} className="odd:bg-white even:bg-slate-100 hover:bg-blue-50 border-b border-slate-100" data-testid={`vitrine-product-row-${i}`}>
                                                <td className="px-2 py-0.5 font-medium truncate max-w-[300px]" title={p.libelle}>{p.libelle}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums text-slate-500">{p.days_sold}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums">{p.qty_natif}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums font-semibold">{formatCurrency(p.ca_natif)}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums text-amber-700">{p.qty_dem || <span className="text-slate-200">—</span>}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums text-amber-700">{p.pct_dem_qty > 0 ? `${p.pct_dem_qty}%` : <span className="text-slate-200">—</span>}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums text-red-600">{p.loss > 0 ? formatCurrency(p.loss) : <span className="text-slate-200">—</span>}</td>
                                                <td className="px-2 py-0.5 text-center tabular-nums">
                                                    {p.median_last_sale ? (
                                                        <span className={`font-semibold ${p.median_last_sale < '15:00' ? 'text-red-600' : p.median_last_sale < closeHour ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                            {p.median_last_sale}
                                                        </span>
                                                    ) : <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="px-2 py-0.5 text-center tabular-nums">
                                                    {p.pct_late !== null ? (
                                                        <span className={p.pct_late >= 50 ? 'text-emerald-600 font-semibold' : 'text-slate-500'}>{p.pct_late}%</span>
                                                    ) : <span className="text-slate-300">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardContent>
                    </Card>

                    {/* Anomalies */}
                    <Card>
                        <CardHeader className="py-3 cursor-pointer" onClick={() => setShowAnomalies(v => !v)} data-testid="vitrine-anomalies-toggle">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <AlertTriangle className={`w-4 h-4 ${recentAnomalies > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                                Anomalies — EAN natif vendu à prix démarqué
                                {recentAnomalies > 0 ? (
                                    <Badge className="bg-red-500 text-white text-[10px] animate-pulse" data-testid="vitrine-anomalies-badge">
                                        {data.anomalies.length} dont {recentAnomalies} cette semaine
                                    </Badge>
                                ) : (
                                    <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="vitrine-anomalies-badge">{data.anomalies.length}</Badge>
                                )}
                                <span className="text-[10px] text-slate-400 font-normal ml-auto">{showAnomalies ? 'Masquer' : 'Afficher'}</span>
                            </CardTitle>
                        </CardHeader>
                        {showAnomalies && (
                            <CardContent className="p-0 overflow-x-auto">
                                {data.anomalies.length === 0 ? (
                                    <div className="py-4 text-center text-slate-400 text-xs">Aucune anomalie sur la période.</div>
                                ) : (
                                    <table className="w-full text-[11px]" data-testid="vitrine-anomalies-table">
                                        <thead className="bg-slate-50 border-b">
                                            <tr className="text-[10px] uppercase text-slate-500">
                                                <th className="text-left px-2 py-1 w-20">Date</th>
                                                <th className="text-left px-2 py-1">Produit</th>
                                                {showEan && <th className="text-left px-2 py-1 w-32">EAN</th>}
                                                <th className="text-right px-2 py-1 w-16">Qté</th>
                                                <th className="text-right px-2 py-1 w-20">Prix payé</th>
                                                <th className="text-right px-2 py-1 w-20">Prix réf.</th>
                                                <th className="text-center px-2 py-1 w-16">Heure</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.anomalies.map((a, i) => (
                                                <tr key={i} className={`border-b border-slate-100 ${a.date >= recentSince ? 'bg-red-50' : 'odd:bg-white even:bg-amber-50/40'}`}>
                                                    <td className="px-2 py-0.5 tabular-nums">
                                                        {a.date.slice(8)}/{a.date.slice(5, 7)}/{a.date.slice(2, 4)}
                                                        {a.date >= recentSince && <Badge className="ml-1.5 bg-red-100 text-red-700 text-[8px] px-1">Récent</Badge>}
                                                    </td>
                                                    <td className="px-2 py-0.5">{a.libelle}</td>
                                                    {showEan && <td className="px-2 py-0.5 font-mono text-slate-500">{a.ean}</td>}
                                                    <td className="px-2 py-0.5 text-right tabular-nums">{a.nb_articles}</td>
                                                    <td className="px-2 py-0.5 text-right tabular-nums text-amber-700 font-semibold">{formatCurrency(a.prix_moyen)}</td>
                                                    <td className="px-2 py-0.5 text-right tabular-nums text-slate-500">{formatCurrency(a.ref_price)}</td>
                                                    <td className="px-2 py-0.5 text-center tabular-nums">{a.last_sale || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardContent>
                        )}
                    </Card>

                    {/* Unknown EANs */}
                    <Card>
                        <CardHeader className="py-3 cursor-pointer" onClick={() => setShowUnknown(v => !v)} data-testid="vitrine-unknown-toggle">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <HelpCircle className="w-4 h-4 text-slate-400" />
                                EAN exclus (produits non Sokuzai)
                                <Badge variant="outline" className="text-[10px]">{data.unknown_eans.length}</Badge>
                                <span className="text-[10px] text-slate-400 font-normal ml-auto">{showUnknown ? 'Masquer' : 'Afficher'}</span>
                            </CardTitle>
                        </CardHeader>
                        {showUnknown && (
                            <CardContent className="p-0 overflow-x-auto">
                                <table className="w-full text-[11px]" data-testid="vitrine-unknown-table">
                                    <thead className="bg-slate-50 border-b">
                                        <tr className="text-[10px] uppercase text-slate-500">
                                            <th className="text-left px-2 py-1 w-36">EAN</th>
                                            <th className="text-left px-2 py-1">Libellé</th>
                                            <th className="text-right px-2 py-1 w-14">Jours</th>
                                            <th className="text-right px-2 py-1 w-14">Qté</th>
                                            <th className="text-right px-2 py-1 w-20">CA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.unknown_eans.map((u) => (
                                            <tr key={u.ean} className="odd:bg-white even:bg-slate-100 border-b border-slate-100">
                                                <td className="px-2 py-0.5 font-mono text-slate-500">{u.ean}</td>
                                                <td className="px-2 py-0.5">{u.libelle}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums">{u.days}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums">{u.qty}</td>
                                                <td className="px-2 py-0.5 text-right tabular-nums">{formatCurrency(u.ca)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        )}
                    </Card>
                </>
            )}

            {periodDay && dayData && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            Journée du {dayDate.slice(8)}/{dayDate.slice(5, 7)}/{dayDate.slice(0, 4)}
                            <Badge variant="outline" className="text-[10px]">{dayData.rows.length} lignes</Badge>
                            {dayData.excluded_count > 0 && (
                                <Badge className="bg-slate-100 text-slate-500 text-[10px]">{dayData.excluded_count} exclues ({formatCurrency(dayData.excluded_ca)})</Badge>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                        {dayData.rows.length === 0 ? (
                            <div className="py-8 text-center text-slate-400 text-sm">Aucune donnée pour cette journée.</div>
                        ) : (
                            <table className="w-full text-[11px]" data-testid="vitrine-day-table">
                                <thead className="bg-slate-50 border-b">
                                    <tr className="text-[10px] uppercase text-slate-500">
                                        <th className="text-center px-2 py-1 w-20">Dern. vente</th>
                                        <th className="text-left px-2 py-1">Produit</th>
                                        <th className="text-center px-2 py-1 w-20">Type</th>
                                        {showEan && <th className="text-left px-2 py-1 w-32">EAN</th>}
                                        <th className="text-right px-2 py-1 w-14">Qté</th>
                                        <th className="text-right px-2 py-1 w-20">Prix moyen</th>
                                        <th className="text-right px-2 py-1 w-20">CA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dayData.rows.map((r, i) => (
                                        <tr key={i} className={`border-b border-slate-100 ${r.kind === 'demarque' ? 'bg-amber-50/50' : 'odd:bg-white even:bg-slate-100'}`}>
                                            <td className="px-2 py-0.5 text-center tabular-nums font-semibold">{r.last_sale || '—'}</td>
                                            <td className="px-2 py-0.5">{r.libelle}</td>
                                            <td className="px-2 py-0.5 text-center">
                                                {r.kind === 'demarque'
                                                    ? <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5">Démarque</Badge>
                                                    : <Badge className="bg-emerald-100 text-emerald-700 text-[9px] px-1.5">Natif</Badge>}
                                            </td>
                                            {showEan && <td className="px-2 py-0.5 font-mono text-slate-500">{r.ean}</td>}
                                            <td className="px-2 py-0.5 text-right tabular-nums">{Math.round(r.nb_articles)}</td>
                                            <td className="px-2 py-0.5 text-right tabular-nums">{formatCurrency(r.prix_moyen)}</td>
                                            <td className="px-2 py-0.5 text-right tabular-nums font-semibold">{formatCurrency(r.ca)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function KpiCard({ label, value, sub, tone }) {
    const toneCls = tone === 'emerald' ? 'text-emerald-600'
        : tone === 'amber' ? 'text-amber-600'
        : tone === 'red' ? 'text-red-600'
        : 'text-slate-900';
    return (
        <Card>
            <CardContent className="py-3 px-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
                <div className={`text-lg font-bold tabular-nums ${toneCls}`}>{value}</div>
                {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
            </CardContent>
        </Card>
    );
}
